// React
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

// MUI
import Grid from "@mui/material/Grid2";
import {
  IconButton,
  InputAdornment,
  Paper,
  Tooltip,
  Typography,
  Stack,
} from "@mui/material";
import {
  ArrowBackIos,
  ArrowForwardIos,
  Clear,
  MenuBookOutlined,
} from "@mui/icons-material";
import {
  CalendarIcon,
  DesktopDatePicker,
  DesktopTimePicker,
  renderDigitalClockTimeView,
  TimeIcon,
} from "@mui/x-date-pickers";

// Styles
import { styles } from "../styles";
import { styles as filterStyles } from "./styles";

// Components
import ClinicFilter from "../Common/ClinicFilter";
import { DateTime } from "luxon";

// Constants
import { MANITOBA_TIMEZONE } from "Constants/TimeConstants";
import { APPOINTMENT_AVAILABILITY_STATUS } from "Constants/AppointmentConstants";
const { ONLINE_AVAILABLE } = APPOINTMENT_AVAILABILITY_STATUS;

const AppointmentFilters = (props) => {
  const {
    successfulClinicAppointments,
    failedClinicAppointments,
    onChange,
    setIsTimeSelected,
    isMBS,
  } = props;

  const { t, i18n } = useTranslation();
  const isFrench = i18n.language === "fr";

  const today = DateTime.now().setZone(MANITOBA_TIMEZONE).toISODate();
  const dateRef = useRef(null);
  const timeRef = useRef(null);

  const [clinics, setClinics] = useState([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [openClinicPicker, setOpenClinicPicker] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openTimePicker, setOpenTimePicker] = useState(false);

  useEffect(() => {
    setIsTimeSelected(!!time);
  }, [setIsTimeSelected, time]);

  // Filter clinics and slots based on selected filters
  useEffect(() => {
    const selectedDate = date?.toISODate();
    const selectedStartHour = time?.hour;
    const endHour = time?.plus({ hours: 1 }).hour;
    const selectedEndHour = endHour === 0 ? 24 : endHour; // to handle hour = 0 at midnight

    const filtered = successfulClinicAppointments
      .filter(
        (clinic) => !clinics.length || clinics.some((c) => c.id === clinic.id)
      )
      .flatMap((clinic) => {
        // Get all relevant dates to check
        const datesToCheck = selectedDate
          ? [selectedDate]
          : Object.keys(clinic.availabilityStatus);

        // For each date, process the logic
        return datesToCheck.map((dateKey) => {
          const status = clinic.availabilityStatus[dateKey];
          let matches = [];

          // check if the date is in the available slots
          if (status === ONLINE_AVAILABLE) {
            matches = clinic.availableSlots.filter((slot) => {
              return slot.date === dateKey;
            });

            if (time) {
              matches = matches.filter((slot) => {
                const slotHour = DateTime.fromFormat(
                  slot.startTime,
                  "hh:mm a"
                ).hour;
                return (
                  slotHour >= selectedStartHour && slotHour < selectedEndHour
                );
              });
            }
            if (!matches.length) return null;
            return {
              ...clinic,
              availableSlots: matches,
              availabilityStatus: {
                [dateKey]: clinic.availabilityStatus[dateKey],
              },
            };
          } else if (
            status === APPOINTMENT_AVAILABILITY_STATUS.WALK_IN_AVAILABLE ||
            status === APPOINTMENT_AVAILABILITY_STATUS.FULLY_BOOKED
          ) {
            let hasAvailableSlots = false;

            // Check if dateKey exists in the keys
            if (
              (clinic.availabilityStatus[dateKey] ===
                APPOINTMENT_AVAILABILITY_STATUS.WALK_IN_AVAILABLE ||
                clinic.availabilityStatus[dateKey] ===
                  APPOINTMENT_AVAILABILITY_STATUS.FULLY_BOOKED) &&
              !time
            ) {
              hasAvailableSlots = true;
              matches = clinic.availableSlots.filter((slot) => {
                return slot.date === dateKey;
              });
            }

            return hasAvailableSlots
              ? {
                  ...clinic,
                  availableSlots: matches,
                  availabilityStatus: {
                    [dateKey]: clinic.availabilityStatus[dateKey],
                  },
                }
              : null;
          }
          return null;
        });
      })
      .filter(Boolean);
    onChange(filtered);
  }, [successfulClinicAppointments, onChange, clinics, date, time, today]);

  const isDateEnabled = (date) => {
    const availableDates = new Set(
      successfulClinicAppointments.flatMap((clinic) =>
        clinic.availableSlots.map((slot) => slot.date)
      )
    );
    availableDates.add(today);
    availableDates.add(
      DateTime.now().setZone(MANITOBA_TIMEZONE).plus({ days: 1 }).toISODate()
    );

    return availableDates.has(date?.toISODate());
  };

  return (
    <Paper sx={styles.availableAppointmentsPaper}>
      {isMBS && (
        <Stack spacing={1} alignItems="flex-start" mb={2}>
          <Stack direction="row" spacing={1} width="auto" alignItems="center">
            <MenuBookOutlined fontSize="large" color="primary" />
            <Typography variant="h3">
              {t("viewMoreAppointmentsModal.availableAppointments")}
            </Typography>
          </Stack>
          <Typography variant="subtitle1">
            {t("availableAppointments.appointmentDescription")}
          </Typography>
        </Stack>
      )}
      <Grid container spacing={2}>
        {/* Clinic Filter */}
        <Grid size={{ xs: 12, md: 4 }}>
          <ClinicFilter
            options={[
              ...successfulClinicAppointments,
              ...failedClinicAppointments,
            ]}
            selectedClinics={clinics}
            setSelectedClinics={setClinics}
            open={openClinicPicker}
            setOpenPicker={setOpenClinicPicker}
          />
        </Grid>
        {/* Date Filter */}
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
          <DesktopDatePicker
            id="date-filter"
            color="secondary"
            sx={filterStyles.gridItem}
            label={
              openDatePicker || date ? t("availableAppointments.date") : ""
            }
            value={date}
            open={openDatePicker}
            onOpen={() => setOpenDatePicker(true)}
            onClose={() => setOpenDatePicker(false)}
            closeOnSelect
            format={isFrench ? "dd MMMM yyyy" : "MMMM dd, yyyy"}
            onChange={(newValue) => setDate(newValue)}
            shouldDisableDate={(date) => !isDateEnabled(date)}
            slotProps={{
              textField: {
                color: "secondary",
                inputRef: dateRef,
                onClick: () => {
                  setOpenDatePicker(!openDatePicker);
                  if (dateRef?.current) {
                    dateRef.current.blur();
                  }
                },
                readOnly: true,
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        sx={filterStyles.icon}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDatePicker(!openDatePicker);
                        }}
                      >
                        <CalendarIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: date && (
                    <InputAdornment position="end">
                      <IconButton
                        sx={filterStyles.clearIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDate(null);
                          setOpenDatePicker(false);
                        }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                inputProps: {
                  ...(date && { sx: { textAlign: "center" } }),
                  value: date
                    ? date.toFormat(isFrench ? "dd MMMM yyyy" : "MMMM dd, yyyy")
                    : "",
                  placeholder:
                    openDatePicker || date
                      ? ""
                      : t("availableAppointments.date"),
                },
              },
              popper: {
                sx: filterStyles.datePickerPopper,
              },
            }}
          />
        </Grid>
        {/* Time Filter */}
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
          <DesktopTimePicker
            id="time-filter"
            color="secondary"
            sx={filterStyles.gridItem}
            label={
              openTimePicker || time ? t("availableAppointments.time") : ""
            }
            value={time}
            timezone={MANITOBA_TIMEZONE}
            open={openTimePicker}
            onOpen={() => setOpenTimePicker(true)}
            onClose={() => setOpenTimePicker(false)}
            onChange={(newValue) => setTime(newValue)}
            closeOnSelect
            viewRenderers={{
              hours: renderDigitalClockTimeView,
              minutes: renderDigitalClockTimeView,
            }}
            timeSteps={{ minutes: 60 }}
            slotProps={{
              textField: {
                color: "secondary",
                inputRef: timeRef,
                onClick: () => {
                  setOpenTimePicker(!openTimePicker);
                  if (timeRef?.current) {
                    timeRef.current.blur();
                  }
                },
                readOnly: true,
                InputProps: {
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <IconButton
                          sx={filterStyles.icon}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenTimePicker(!openTimePicker);
                          }}
                        >
                          <TimeIcon />
                        </IconButton>
                      </InputAdornment>
                      {time && (
                        <InputAdornment position="start">
                          <Tooltip title={t("availableAppointments.prevHour")}>
                            <IconButton
                              sx={filterStyles.icon}
                              onClick={(e) => {
                                e.stopPropagation();
                                setTime(time.minus({ hours: 1 }));
                              }}
                            >
                              <ArrowBackIos />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )}
                    </>
                  ),
                  endAdornment: time && (
                    <>
                      <InputAdornment position="end">
                        <Tooltip title={t("availableAppointments.nextHour")}>
                          <IconButton
                            sx={filterStyles.icon}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTime(time.plus({ hours: 1 }));
                            }}
                          >
                            <ArrowForwardIos />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                      <InputAdornment position="end">
                        <IconButton
                          sx={filterStyles.clearIcon}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTime(null);
                            setOpenTimePicker(false);
                          }}
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    </>
                  ),
                },
                inputProps: {
                  ...(time && { sx: { textAlign: "center" } }),
                  value: time
                    ? time.toFormat(isFrench ? "HH:mm" : "hh:mm a")
                    : "",
                  placeholder:
                    openTimePicker || time
                      ? ""
                      : t("availableAppointments.time"),
                },
              },
              popper: {
                sx: filterStyles.timePickerPopper,
              },
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AppointmentFilters;
